import { lazy, StrictMode, Suspense, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams
} from 'react-router-dom';
import AuthGate from './components/AuthGate.jsx';
import ReportOverlay from './components/ReportOverlay.jsx';
import { FeatureContext, useFeatures } from './hooks/index.js';
import { PositionsProvider } from './hooks/positions.jsx';
import { getFeatures } from './lib/api.js';
import { useCloseStock } from './lib/navigation.js';
import WorkspaceLayout from './components/Chrome.jsx';
import ExitAlertDialog from './components/ExitAlertDialog.jsx';
import WorkspaceStatus from './components/ui/WorkspaceStatus.jsx';
import PageIntro from './components/ui/PageIntro.jsx';
import ContentBoundary from './components/ui/ContentBoundary.jsx';
import { LoadingState } from './components/ui/Primitives.jsx';
import '@fontsource-variable/inter';
import './styles/theme.css';
import './styles/workspace.css';
import './styles/journeys.css';

const App = lazy(() => import('./App.jsx'));
const InvestmentsPage = lazy(() => import('./components/InvestmentsPage.jsx'));
const FundsPage = lazy(() => import('./components/FundsPage.jsx'));
const SwingPage = lazy(() => import('./components/SwingPage.jsx'));
const UsersPage = lazy(() => import('./components/UsersPage.jsx'));
const UsagePage = lazy(() => import('./components/UsagePage.jsx'));

function FeatureGate({ children }) {
  const [features, setFeatures] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getFeatures().then(
      (data) => { if (!cancelled) setFeatures(data); },
      (err) => { if (!cancelled) setError(err.message); }
    );
    return () => { cancelled = true; };
  }, [attempt]);

  if (error) return <WorkspaceStatus title="The workspace is unavailable." error={error} onRetry={() => setAttempt((value) => value + 1)} />;
  if (!features) return <WorkspaceStatus title="Preparing your workspace">Loading the research tools available to you.</WorkspaceStatus>;
  return <FeatureContext.Provider value={features}>{children}</FeatureContext.Provider>;
}

function StockOverlayRoute() {
  const { symbol } = useParams();
  const location = useLocation();
  const close = useCloseStock();
  return <ReportOverlay symbol={symbol} holdingYears={location.state?.holdingYears ?? 3} onClose={close} />;
}

function Shell() {
  const location = useLocation();
  const features = useFeatures();
  // Set when a report was opened from another page; that page keeps rendering
  // underneath, so its state and scroll position survive being closed.
  const background = location.state?.background;

  // Straight into the first tool the account can use. The old landing page
  // asked people to choose a "goal" before anything would load, which is a
  // click that never told anyone anything they could not see in the sidebar.
  const defaultPath = features.stocks ? "/stocks" : features.swing ? "/swing" : features.funds ? "/funds" : "/stocks";
  const stocks = features.stocks ? <App /> : <Navigate to={defaultPath} replace />;

  if (!features.stocks && !features.swing && !features.funds) {
    return <WorkspaceStatus title="No research tools are enabled." error="Contact your workspace administrator to enable a section." />;
  }

  return (
    <WorkspaceLayout>
      <ContentBoundary key={background?.pathname ?? location.pathname} errorClassName="shell">
        <Suspense fallback={
          <div className="shell">
            <LoadingState title="Opening this workspace view">Loading the tools for this section. Navigation is still available.</LoadingState>
          </div>
        }>
          <Routes location={background ?? location}>
            <Route path="/" element={<Navigate to={defaultPath} replace />} />
            <Route path="/stocks" element={stocks} />
            <Route path="/stock/:symbol" element={stocks} />
            <Route path="/swing" element={features.swing ? <SwingPage /> : <Navigate to={defaultPath} replace />} />
            <Route path="/investments" element={<InvestmentsPage />} />
            <Route path="/funds" element={features.funds ? <FundsPage /> : <Navigate to={defaultPath} replace />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/usage" element={<UsagePage />} />
            <Route path="*" element={
              <div className="shell">
                <PageIntro eyebrow="NOT FOUND" title="This page took a different path." description="The address may have changed." icon="info" />
                <Link className="btn primary" to={defaultPath}>Back to your workspace</Link>
              </div>
            } />
          </Routes>
        </Suspense>
      </ContentBoundary>

      {background && features.stocks && (
        <Routes>
          <Route path="/stock/:symbol" element={<StockOverlayRoute />} />
        </Routes>
      )}
      <ExitAlertDialog />
    </WorkspaceLayout>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthGate>
        <FeatureGate>
          <PositionsProvider>
            <Shell />
          </PositionsProvider>
        </FeatureGate>
      </AuthGate>
    </BrowserRouter>
  </StrictMode>
);
